import { createLogic } from 'redux-logic';
import { toastr } from 'react-redux-toastr';

import { getApplicationStorage, setApplicationStorage, converToNumber } from 'xpi-black-ui/Utils/helpers';

import { CREATE_OPERATION_URL, CREATE_OPERATION_FILTERS_URL, QUOTE_OPERATION_URL, VALIDATE_PRICE_OPERATION_URL } from 'utils/api';

import { normalizeFilters, normalizeOperationDetail, updateOperation } from '../models/create-operation';

import {
  LOAD_CREATE_OPERATION_DATA,
  SAVE_OPERATION,
  QUOTE_OPERATION,
  DELETE_OPERATION,
  UPDATE_PRICE_QUOTE_OPERATION,
} from '../constants/create-operation';

import {
  SaveOperationSuccess,
  SaveOperationError,
  DeleteOperationSuccess,
  QuoteOperationSuccess,
  QuoteOperationError,
  UpdateQuoteOperation,
  CreateOperationDataLoaded,
  CreateOperationDataError,
} from '../actions/create-operation';

let cotizadorStorage = getApplicationStorage();

const saveOperationInStorage = (operation) => {
  const quoteOperations = cotizadorStorage.quoteOperations || [];

  cotizadorStorage = {
    ...cotizadorStorage,
    quoteOperations: [...quoteOperations, operation],
  };

  setApplicationStorage(cotizadorStorage);
};

const removeOperationFromStorage = (savedOperation) => {
  const { quoteOperations } = cotizadorStorage;

  cotizadorStorage = {
    ...cotizadorStorage,
    quoteOperations: quoteOperations.filter(operation => operation.operationId !== savedOperation.operationId),
  };

  setApplicationStorage(cotizadorStorage);
};

const onLoadOpertionsByStoragedQuotes = (normalizedFilters, requestUtil, dispatch) => new Promise((resolve) => {
  onLoadQuoteOperation(requestUtil, cotizadorStorage.quoteOperations.map(quoteOperation => ({
    asset: quoteOperation.ativo,
    withStock: quoteOperation.withStock,
    structure: quoteOperation.estrutura,
    maturityDate: quoteOperation.vencimento,
  })))
    .then((response) => {
      let normalizedOperations = [];
      if (!response[0].error) {
        normalizedOperations = response.map(operation => (
          normalizeOperationDetail(operation)
        ));
      }
      dispatch(CreateOperationDataLoaded({ filters: normalizedFilters, quoteOperations: normalizedOperations }));
      resolve();
    });
});

const onLoadCreateOpertionData = (requestUtil, dispatch, response) => new Promise((resolve) => {
  const { error, filtros } = response;
  if (!error) {
    const normalizedFilters = normalizeFilters(filtros);

    if (cotizadorStorage.quoteOperations === undefined || cotizadorStorage.quoteOperations.length === 0) {
      dispatch(CreateOperationDataLoaded({ filters: normalizedFilters }));
      resolve();
    } else {
      onLoadOpertionsByStoragedQuotes(normalizedFilters, requestUtil, dispatch)
        .then(() => {
          resolve();
        });
    }
  } else {
    toastr.error('Tenta novamente', 'Falha ao carregar as informações');
    dispatch(CreateOperationDataError());
  }
});

const LoadCreateOperationData = createLogic({
  type: LOAD_CREATE_OPERATION_DATA,
  latest: true,

  process({ requestUtil }, dispatch, done) {
    requestUtil(CREATE_OPERATION_FILTERS_URL)
      .then((response) => {
        onLoadCreateOpertionData(requestUtil, dispatch, response)
          .then(() => {
            done();
          });
      });
  },
});

const onLoadQuoteOperation = (requestUtil, operations) => {
  const operationCalls = operations.map((operation) => {
    const { asset, withStock, structure, maturityDate } = operation;
    const params = `?Ativo=${asset}&ComPapel=${withStock}&Estrutura=${structure}&Vencimento=${maturityDate}`;

    return requestUtil(QUOTE_OPERATION_URL + params);
  });

  return Promise.all(operationCalls);
};

const QuoteOperation = createLogic({
  latest: true,
  type: QUOTE_OPERATION,
  process({ requestUtil, action }, dispatch, done) {
    const { operation } = action;

    onLoadQuoteOperation(requestUtil, [operation])
      .then((response) => {
        if (response[0].error) {
          const message = response[0].error.response ? response[0].error.response.data : response[0].error.message;
          toastr.error('Erro', message);
          dispatch(QuoteOperationError());
        } else {
          const normalizedOperations = normalizeOperationDetail(response[0]);
          saveOperationInStorage(normalizedOperations);
          dispatch(QuoteOperationSuccess(normalizedOperations));
        }
      })
      .then(done);
  },
});

const UpdatePriceOperation = createLogic({
  type: UPDATE_PRICE_QUOTE_OPERATION,
  latest: true,

  process({ requestUtil, action }, dispatch, done) {
    const { operation } = action;
    const structures = operation.configuracaoEstrutura.filter(structure => !structure.isStock);
    const options = ({
      method: 'PATCH',
      data: {
        ativo: operation.ativo,
        pernas: structures.map(structure => ({
          natureza: structure.natureza,
          tipo: structure.tipo,
          strike: structure.strike.value,
          strikeIndicativo: structure.strike.strikeIndicativeNumeric,
        })),
        vencimento: operation.vencimento,
      },
    });

    requestUtil(VALIDATE_PRICE_OPERATION_URL, options)
      .then((response) => {
        if (!response.error) {
          const { sucesso, resultado } = response;
          const { dados, mensagens } = resultado;

          const updatedOperation = updateOperation(operation, sucesso, dados.comissaoMaxima, dados.comissaoMinima, dados.precoCareca, dados.ativo.ultimoPreco);

          if (!sucesso) {
            dispatch(UpdateQuoteOperation(updatedOperation));
            const messages = mensagens.map(message => message.descricao).join(' ');
            toastr.error('Tenta novamente', messages);
          } else {
            dispatch(UpdateQuoteOperation(updatedOperation));
          }
        } else {
          toastr.error('Tenta novamente', 'Falha ao carregar os dados da operação');
          dispatch(UpdateQuoteOperation(operation));
        }
      })
      .then(done);
  },
});

const SaveOperation = createLogic({
  type: SAVE_OPERATION,
  latest: true,

  process({ requestUtil, action }, dispatch, done) {
    const { operation, comission } = action;
    const structures = operation.configuracaoEstrutura.filter(structure => !structure.isStock);

    const options = ({
      method: 'POST',
      data: {
        estrutura: operation.estrutura,
        ativo: operation.ativo,
        pernas: structures.map(structure => ({
          natureza: structure.natureza,
          tipo: structure.tipo,
          strike: structure.strike.value,
          strikeIndicativo: converToNumber(structure.strike.strikeIndicative),
        })),
        vencimento: operation.vencimento,
        comPapel: operation.withStock,
        comissao: comission,
      },
    });

    requestUtil(CREATE_OPERATION_URL, options)
      .then((response) => {
        if (!response.error) {
          const { sucesso, resultado } = response;
          const { mensagens } = resultado;

          if (!sucesso) {
            dispatch(SaveOperationError());
            const messages = mensagens.map(message => message.descricao).join(' ');
            toastr.error('Tenta novamente', messages);
          } else {
            dispatch(SaveOperationSuccess(operation));
            removeOperationFromStorage(operation);
          }
        } else {
          dispatch(SaveOperationError());
          toastr.error('Tenta novamente', 'Falha ao salvar a operação');
        }
      })
      .then(done);
  },
});

const DeleteOperation = createLogic({
  type: DELETE_OPERATION,
  latest: true,

  process({ action }, dispatch, done) {
    setTimeout(() => {
      removeOperationFromStorage(action.operation);
      dispatch(DeleteOperationSuccess(action.operation));
      done();
    }, 500);
  },
});

export default [LoadCreateOperationData, SaveOperation, QuoteOperation, DeleteOperation, UpdatePriceOperation];
